import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { useLoaderData, Link } from "@remix-run/react";
import { json } from "@remix-run/cloudflare";
import { ArrowLeft, Wrench, Home, ChevronRight } from "lucide-react";
import { createSupabaseAdminClient } from '~/lib/supabase.server'
import type { Database } from '~/types/database'

type Category = Database['public']['Tables']['categories']['Row']

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const categoryName = data?.category?.name || "Category";
  return [
    { title: `${categoryName} | stellarblademod.com` },
    { 
      name: "description", 
      content: `Browse ${categoryName} mods for Stellar Blade. Coming soon - enhanced category browsing experience.` 
    },
    { name: "keywords", content: `${categoryName}, stellar blade mods, mod category, coming soon` },
    
    // Canonical URL - 使用slug
    { tagName: "link", rel: "canonical", href: `https://stellarblademod.com/mods/category/${data?.category?.slug}` },
  ];
};

export async function loader({ params, context }: LoaderFunctionArgs) {
  const categorySlug = params.slug;
  
  if (!categorySlug) {
    throw new Response('Category slug is required', { status: 400 })
  }

  // 处理本地开发环境和生产环境的差异
  const isProduction = process.env.NODE_ENV === 'production';
  const env = isProduction ? (context as any)?.cloudflare?.env : {
    SUPABASE_URL: process.env.SUPABASE_URL,
    SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
    SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
  };
  
  const supabase = createSupabaseAdminClient(env)

  // 通过slug获取分类信息
  const { data: category, error: categoryError } = await supabase
    .from('categories')
    .select('*')
    .eq('slug', categorySlug)
    .single()

  if (categoryError || !category) {
    console.error('Category query error:', categoryError)
    throw new Response('Category not found', { status: 404 })
  }

  return json({ category });
}

export default function CategoryComingSoonPage() {
  const { category } = useLoaderData<typeof loader>();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 面包屑导航 */}
        <nav className="mb-6">
          <div className="bg-gradient-to-r from-slate-800/60 to-slate-700/60 backdrop-blur-sm border border-slate-600/30 rounded-lg px-4 py-3 shadow-lg">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link 
                  to="/" 
                  className="flex items-center gap-1.5 text-slate-300 hover:text-cyan-300 transition-colors font-medium"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>
              </li>
              <li className="flex items-center">
                <ChevronRight className="w-4 h-4 text-slate-500 mx-1" />
                <Link 
                  to="/mods" 
                  className="text-slate-300 hover:text-cyan-300 transition-colors font-medium"
                >
                  Mods
                </Link>
              </li>
              <li className="flex items-center">
                <ChevronRight className="w-4 h-4 text-slate-500 mx-1" />
                <span className="text-cyan-300 font-semibold">
                  {category.name}
                </span>
              </li>
            </ol>
          </div>
        </nav>

        {/* Coming Soon Content */}
        <div className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm border border-slate-600/50 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-8 md:p-12 text-center">
            {/* Icon */}
            <div className="w-24 h-24 mx-auto bg-gradient-to-r from-orange-600/30 to-yellow-600/30 rounded-full flex items-center justify-center mb-8 border border-orange-500/50">
              <Wrench className="w-12 h-12 text-orange-400" />
            </div>

            {/* Category Name */}
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-300 via-yellow-300 to-orange-300 bg-clip-text text-transparent mb-4">
              {category.name}
            </h1>

            {/* Category Description */}
            {category.description && (
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                {category.description}
              </p>
            )}

            {/* Coming Soon Message */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent mb-4">
                🚧 Category Page Coming Soon
              </h2>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
                We're crafting an amazing browsing experience for <span className="text-orange-300 font-semibold">{category.name}</span> mods. 
                Get ready for advanced filtering, sorting, and discovery features that will help you find exactly what you're looking for!
              </p>
            </div>

            {/* Features Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-lg p-6">
                <div className="w-12 h-12 bg-blue-500/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-blue-300 mb-2">Smart Filtering</h3>
                <p className="text-slate-300 text-sm">Filter by rating, download count, date, and compatibility</p>
              </div>

              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg p-6">
                <div className="w-12 h-12 bg-purple-500/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-purple-300 mb-2">Advanced Search</h3>
                <p className="text-slate-300 text-sm">Find mods by name, author, tags, and description</p>
              </div>

              <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-lg p-6">
                <div className="w-12 h-12 bg-green-500/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-green-300 mb-2">Personalized</h3>
                <p className="text-slate-300 text-sm">Recommendations based on your preferences and favorites</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/mods"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-bold transition-all duration-300 flex items-center gap-3 shadow-2xl hover:shadow-cyan-500/25 border border-cyan-400/30 transform hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5" />
                Browse All Mods
              </Link>

              <Link
                to="/"
                className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 border border-slate-500/50 transform hover:scale-105"
              >
                <Home className="w-5 h-5" />
                Return Home
              </Link>
            </div>

            {/* Progress Indicator */}
            <div className="mt-12 pt-8 border-t border-slate-600/50">
              <p className="text-slate-400 text-sm mb-4">Development Progress</p>
              <div className="bg-slate-700/50 rounded-full h-3 max-w-md mx-auto">
                <div className="bg-gradient-to-r from-orange-500 to-yellow-500 h-3 rounded-full" style={{ width: '35%' }}></div>
              </div>
              <p className="text-slate-400 text-xs mt-2">Category pages are currently in development</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}